import { HttpContextToken, HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { iif, Observable, throwError } from 'rxjs';
import { catchError, finalize, tap, timeout } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { LoadingBarService } from '@ngx-loading-bar/core';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { environment } from '@environment';
import { GeneratedSessionService } from '@core-shared/generated/services';
import { SessionService } from '@core-shared/services/session.service';
import * as Sentry from '@sentry/angular';
import { tryParseJson } from '@core-shared/misc/helpers';
import { API_BUILD_VERSION_HEADER, UpdateService } from '@core-shared/services/update.service';


@Injectable()
export class HttpInterceptorService implements HttpInterceptor {

  private logoutToastRef?: ActiveToast<unknown>;
  private readonly pathsToExcludeFromErrorHandling: string[];

  constructor(private sessionService: SessionService,
    private router: Router,
    private translateService: TranslateService,
    private toastr: ToastrService,
    private loader: LoadingBarService,
    private updateService: UpdateService) {
    this.pathsToExcludeFromErrorHandling = [
      GeneratedSessionService.SignInPath,
      GeneratedSessionService.SignInExternalPath
      /*       GeneratedUserAccountService.EnableTwoFactorAuthenticatorPath,
            GeneratedUserAccountService.EnableTwoFactorEmailPath,
            GeneratedUserAccountService.DisableTwoFactorEmailPath,
            GeneratedUserAccountService.RemoveTwoFactorAuthenticatorPath,
            GeneratedUserAccountService.ChangePasswordPath,
            GeneratedUserAccountService.AssignExternalLoginPath,
            GeneratedUserAccountService.UnassignExternalLoginPath,
            GeneratedUserAccountService.GenerateTwoFactorRecoveryCodesPath */
    ];
    // const sessionInfo = isPlatformBrowser(this.platformId) ? localStorage.getItem(nameof<SessionService>((s) => s.$signInInfo)) : undefined;
  }


  public intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<any> {
    const isApiRequest = req.url.includes('/api/');
    if (isApiRequest) {
      req = req.clone({
        setHeaders: {
          'Accept-Language': req.headers.get('Accept-Language') ?? this.translateService.currentLang() ?? 'en-US',
          'X-API-Version': environment.apiVersion // TODO environmentService?
        }
      });
    }

    // Every API response carries the API's build number, error responses included (#6388)
    const response$ = !isApiRequest
      ? next.handle(req)
      : next.handle(req).pipe(tap({
        next: (event) => {
          if (event instanceof HttpResponse) {
            this.updateService.checkApiVersion(event.headers.get(API_BUILD_VERSION_HEADER));
          }
        },
        error: (error) => {
          if (error instanceof HttpErrorResponse) {
            this.updateService.checkApiVersion(error.headers.get(API_BUILD_VERSION_HEADER));
          }
        }
      }));

    // Pass login request without modification
    // TODO ANPASSEN
    if (!this.sessionService.$sessionInfo.value) {
      return response$;
    }

    // TODO try login via one-click-login before logging out

    let started = false;
    const ref = this.loader.useRef('http');
    return response$
      .pipe(tap(() => {
        if (!started) {
          ref.start();
          started = true;
        }
      }),
        (source$) =>
          iif(
            () => !req.url.includes('/api/binaries/auctions/'),
            source$.pipe(timeout(30000)),
            source$
          ),
        finalize(() => ref.complete()),
        catchError((error) => this.handleError(error, req)));
  }

  private handleError(error: HttpErrorResponse, req: HttpRequest<unknown>): Observable<string> {
    let logSentry = true;
    if (error.status === 401 && !this.pathsToExcludeFromErrorHandling.includes(req.url)) {
      this.logoutToastRef?.toastRef.close();
      // TODO TRANSLATE
      this.logoutToastRef = this.toastr.info('Sitzung abgelaufen.');
      this.sessionService.signOut(this.router.url);
      logSentry = false;
    }
    if (logSentry && !req.context.get(SKIP_SENTRY_LOGGING)) {
      Sentry.captureException(error);
    }
    const normalizedBody = tryParseJson(error.error);
    const normalizedError = normalizedBody === error.error
      ? error
      : new HttpErrorResponse({
        error: normalizedBody,
        headers: error.headers,
        status: error.status,
        statusText: error.statusText,
        url: error.url ?? undefined
      });

    return throwError(() => normalizedError);
  }

}

export const SKIP_SENTRY_LOGGING = new HttpContextToken<boolean>(() => false);
